import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Link } from '@mui/material';

interface ExpandableTextProps {
  text: string;
  maxWidth?: number | string;
  maxLines?: number;
}

export const ExpandableText: React.FC<ExpandableTextProps> = ({ 
  text, 
  maxWidth = 200,
  maxLines = 1,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const element = textRef.current;
        const isOverflow = maxLines === 1 
          ? element.scrollWidth > element.clientWidth
          : element.scrollHeight > element.clientHeight;
        setIsOverflowing(isOverflow);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text, maxWidth, maxLines]);

  if (!text) return <Typography component="span" variant="body2">N/A</Typography>;

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography
        ref={textRef}
        component="span"
        variant="body2"
        sx={{
          overflow: 'hidden',
          textOverflow: isExpanded ? 'clip' : 'ellipsis',
          display: maxLines > 1 ? '-webkit-box' : 'inline',
          WebkitLineClamp: isExpanded ? 'unset' : maxLines,
          WebkitBoxOrient: maxLines > 1 ? 'vertical' : undefined,
          whiteSpace: maxLines === 1 ? (isExpanded ? 'normal' : 'nowrap') : 'normal',
          maxWidth: isExpanded ? 'none' : maxWidth,
          wordBreak: isExpanded ? 'break-word' : 'normal',
        }}
      >
        {text}
      </Typography>
      {isOverflowing && (
        <Link
          component="button"
          variant="body2"
          onClick={toggleExpand}
          sx={{
            cursor: 'pointer',
            textDecoration: 'none',
            color: 'primary.main',
            fontSize: '0.875rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {isExpanded ? '...less' : '...more'}
        </Link>
      )}
    </Box>
  );
};